import {useSyncExternalStore} from 'react';

/**
 * Установка приложения из браузера (PWA). Событие beforeinstallprompt приходит один раз
 * сразу после загрузки страницы, поэтому слушатель вешается при старте приложения
 * (initInstallCapture в main.tsx), а страница установки читает сохранённое событие.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform: string}>;
}

export type InstallStatus = 'idle' | 'opening' | 'accepted' | 'dismissed';

interface InstallState {
  canPrompt: boolean;
  installed: boolean;
  status: InstallStatus;
}

let deferred: BeforeInstallPromptEvent | null = null;
let state: InstallState = {canPrompt: false, installed: detectInstalled(), status: 'idle'};
const listeners = new Set<() => void>();

function detectInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone = (navigator as Navigator & {standalone?: boolean}).standalone === true;
  return standalone || iosStandalone;
}

function set(next: Partial<InstallState>): void {
  state = {...state, ...next};
  listeners.forEach((fn) => fn());
}

export function initInstallCapture(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    set({canPrompt: true});
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    set({canPrompt: false, installed: true, status: 'accepted'});
  });
}

export async function promptInstall(): Promise<InstallStatus> {
  if (!deferred) return 'idle';
  set({status: 'opening'});
  try {
    await deferred.prompt();
    const choice = await deferred.userChoice;
    deferred = null;
    set({canPrompt: false, status: choice.outcome === 'accepted' ? 'accepted' : 'dismissed'});
    return choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
  } catch {
    set({status: 'dismissed'});
    return 'dismissed';
  }
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useInstall(): InstallState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}
